/**
 * Sales Performance round 2, stage 1: who each IQ salesperson is on the org board.
 * Pure and side-effect free, so the browser, the derived-data script and (stage 5) the backend all share it.
 *
 *   rep -> user (userMapping[rep].orgUserKey | orgUserRef) -> hat (orgHats[k].assignedToArray)
 *       -> post (hat.positionArray, a partKey) -> ancestors (parentPartKey, else parentRef / path)
 *
 * Identity is the partKey, never the numeric id (ids repeat on real boards). A rep who is not on the board
 * is 'unknown': the kind is never guessed from the rep number (Flash: a road rep can carry either range).
 */

export type SpPersonKind = 'iss' | 'pss' | 'road' | 'manager' | 'unknown';

export interface SpPartRef {
    key: string;
    name: string;
}

export interface SpPerson {
    uid?: string;
    kind: SpPersonKind;
    post?: SpPartRef;
    team?: SpPartRef;
    division?: SpPartRef;
}

export type SpPeople = Record<string, SpPerson>;

/** The parts of an OrgPart this reads. */
export interface OrgPartLike {
    partKey: string;
    name?: string;
    partType?: string;
    parentPartKey?: string;
    parentRef?: string;
    path?: string;
}

export interface SpMappingLike {
    repNum?: string | number;
    orgUserKey?: string;
    /** a Firestore DocumentReference, or what is left of one after JSON; only its last segment is read */
    orgUserRef?: unknown;
}

export interface SpPeopleInput {
    userMapping: Record<string, SpMappingLike>;
    /** the org doc's embedded hats map: `hatsKeys` plus one body per key */
    orgHats: Record<string, unknown>;
    orgParts: OrgPartLike[];
}

interface HatLike {
    positionArray?: string[];
    assignedToArray?: string[];
    orgPosition?: { partKey?: string };
    isSoftDeleted?: boolean;
}

/** Preference when a person holds several posts: a sales post beats a manager post beats anything else. */
const RANK: Record<SpPersonKind, number> = { iss: 3, pss: 3, road: 3, manager: 2, unknown: 0 };

/** What kind of salesperson a post name makes. 'unknown' means the name says nothing about sales. */
export function kindOfPost(postName: string | undefined | null): SpPersonKind {
    const n = (postName || '').trim().toLowerCase();
    if (!n) return 'unknown';
    if (n === 'internal sales staff') return 'iss';
    if (n === 'public sales staff') return 'pss';
    if (/\bmanager\b|\bi\/c$|^head of\b|^chief\b|\bcoordinator$/.test(n)) return 'manager';
    if (/\brep$/.test(n)) return 'road';
    return 'unknown';
}

/** The user id a mapping points at, from orgUserKey or the reference's last path segment. */
export function userKeyOf(m: SpMappingLike | undefined): string | undefined {
    if (!m) return undefined;
    if (m.orgUserKey) return m.orgUserKey;
    const ref = m.orgUserRef as
        | { id?: string; path?: string; _path?: { segments?: string[] } }
        | string
        | null
        | undefined;
    if (!ref) return undefined;
    if (typeof ref === 'string') return ref.split('/').filter(Boolean).pop();
    if (ref.id) return ref.id;
    if (ref.path) return ref.path.split('/').filter(Boolean).pop();
    return ref._path?.segments?.[ref._path.segments.length - 1];
}

const toRef = (p: OrgPartLike): SpPartRef => ({ key: p.partKey, name: (p.name || '').trim() });

export function buildPeople(input: SpPeopleInput): SpPeople {
    const byKey = new Map<string, OrgPartLike>();
    const byPath = new Map<string, OrgPartLike>();
    for (const p of input.orgParts || []) {
        if (!p?.partKey) continue;
        byKey.set(p.partKey, p);
        if (p.path) byPath.set(p.path, p);
    }
    const parentOf = (p: OrgPartLike): OrgPartLike | undefined =>
        (p.parentPartKey && byKey.get(p.parentPartKey)) || (p.parentRef ? byPath.get(p.parentRef) : undefined);
    /** the part's ancestors, nearest first; a cycle in bad data ends the walk */
    const ancestors = (p: OrgPartLike): OrgPartLike[] => {
        const out: OrgPartLike[] = [];
        const seen = new Set([p.partKey]);
        for (let c = parentOf(p); c && !seen.has(c.partKey); c = parentOf(c)) {
            seen.add(c.partKey);
            out.push(c);
        }
        return out;
    };

    // user -> the posts of every live hat they are assigned to
    const postsOfUser = new Map<string, Set<string>>();
    const hatKeys = ((input.orgHats?.['hatsKeys'] as string[]) || []).slice();
    for (const k of hatKeys) {
        const hat = input.orgHats[k] as HatLike | undefined;
        if (!hat || hat.isSoftDeleted) continue;
        const posts = hat.positionArray?.length
            ? hat.positionArray
            : hat.orgPosition?.partKey
              ? [hat.orgPosition.partKey]
              : [];
        for (const uid of hat.assignedToArray || []) {
            const set = postsOfUser.get(uid) || new Set<string>();
            posts.forEach((pk) => set.add(pk));
            postsOfUser.set(uid, set);
        }
    }

    const people: SpPeople = {};
    for (const [rep, m] of Object.entries(input.userMapping || {})) {
        const uid = userKeyOf(m);
        const person: SpPerson = { kind: 'unknown' };
        if (uid) person.uid = uid;
        let best: { part: OrgPartLike; kind: SpPersonKind; depth: number } | null = null;
        for (const pk of postsOfUser.get(uid || '') || []) {
            const part = byKey.get(pk);
            if (!part) continue;
            const cand = { part, kind: kindOfPost(part.name), depth: ancestors(part).length };
            if (
                !best ||
                RANK[cand.kind] > RANK[best.kind] ||
                (RANK[cand.kind] === RANK[best.kind] &&
                    (cand.depth > best.depth || (cand.depth === best.depth && cand.part.partKey < best.part.partKey)))
            )
                best = cand;
        }
        if (best) {
            person.kind = best.kind;
            person.post = toRef(best.part);
            const up = ancestors(best.part);
            const team = up.find((a) => a.partType === 'section' || a.partType === 'department');
            // the division above the team, or above the post when it sits straight under one
            const division = up.find((a) => a.partType === 'division');
            if (team) person.team = toRef(team);
            if (division) person.division = toRef(division);
        }
        people[rep] = person;
    }
    return people;
}
